import React, { useState, useEffect } from "react";
import { Link, useMatch } from "react-router-dom";
import logo from "../assets/lna.png";

// Constants
const MOBILE_BREAKPOINT = 768;
const DEBOUNCE_DELAY = 100;
const MAX_WIDTH = 1400; // Max width for centered content

// Custom hook for window width with debounce
const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    let timeoutId;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, DEBOUNCE_DELAY);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return windowWidth;
};

// Custom hook for scroll detection
const useScrollDetection = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return isScrolled;
};

// NavLink component with active state
const NavLink = ({ to, children, onClick, isMobile }) => {
  const match = useMatch(to);
  
  return (
    <li style={{ width: isMobile ? "100%" : "auto" }}>
      <Link
        to={to}
        style={{
          ...styles.link,
          ...(isMobile ? styles.mobileLink : styles.desktopLink),
          ...(match ? styles.activeLink : {}),
        }}
        onClick={onClick}
      >
        {children}
      </Link>
    </li>
  );
};

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const windowWidth = useWindowWidth();
  const isScrolled = useScrollDetection();
  const isMobile = windowWidth <= MOBILE_BREAKPOINT;

  // Handle body scroll lock
  useEffect(() => {
    if (isMobile && isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile, isMobileMenuOpen]);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About Us" },
    { to: "/membership", label: "Membership" },
    { to: "/education", label: "Education & Training" },
    { to: "/affiliation", label: "Affiliation" },
    { to: "/advocacy", label: "Advocacy" },
    { to: "/events", label: "Events" },
    { to: "/resources", label: "Resources" },
    { to: "/careers", label: "Careers" },
  ];

  return (
    <nav
      style={{
        ...styles.navbar,
        ...(isScrolled && styles.navbarScrolled),
      }}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Centered Container */}
      <div style={styles.container}>
        {/* Logo */}
        <div style={styles.logoContainer}>
          <Link to="/adminLogin" onClick={closeMobileMenu} style={styles.logoLink}>
            <div style={styles.logoWrapper}>
              <img 
                src={logo} 
                alt="LENMO Association Logo" 
                style={styles.logo}
              />
              <span style={styles.logoText}>LENMO</span>
            </div>
          </Link>
        </div>

        {/* Hamburger Menu - Mobile Only */}
        {isMobile && (
          <button
            style={styles.hamburger}
            onClick={toggleMobileMenu}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="navigation-menu"
          >
            <div style={getHamburgerBarStyle(isMobileMenuOpen, 1)} />
            <div style={getHamburgerBarStyle(isMobileMenuOpen, 2)} />
            <div style={getHamburgerBarStyle(isMobileMenuOpen, 3)} />
          </button>
        )}

        {/* Navigation Links */}
        <ul
          id="navigation-menu"
          style={{
            ...styles.navLinks,
            ...(isMobile
              ? {
                  position: "fixed",
                  top: "60px",
                  right: isMobileMenuOpen ? "0" : "-100%",
                  backgroundColor: "#001f54",
                  width: "280px",
                  height: "calc(100vh - 60px)",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: "2rem 1.5rem",
                  gap: "0.5rem",
                  boxShadow: isMobileMenuOpen ? "-5px 0 25px rgba(0,0,0,0.3)" : "none",
                  transition: "right 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  zIndex: 1001,
                  overflowY: "auto",
                }
              : {}),
          }}
        >
          {navItems.map(({ to, label }) => (
            <NavLink 
              key={to} 
              to={to} 
              onClick={closeMobileMenu}
              isMobile={isMobile}
            >
              {label}
            </NavLink>
          ))}
        </ul>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          onClick={closeMobileMenu}
          style={styles.overlay}
          aria-hidden="true"
        />
      )}
    </nav>
  );
};

// Hamburger animation styles
const getHamburgerBarStyle = (isOpen, index) => {
  const baseStyle = {
    width: "22px",
    height: "2px",
    backgroundColor: "#FFD700",
    margin: "3px 0",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    borderRadius: "2px",
  };

  if (isOpen) {
    switch (index) {
      case 1:
        return {
          ...baseStyle,
          transform: "rotate(45deg) translate(4px, 4px)",
        };
      case 2:
        return {
          ...baseStyle,
          opacity: 0,
          transform: "translateX(-8px)",
        };
      case 3:
        return {
          ...baseStyle,
          transform: "rotate(-45deg) translate(6px, -5px)",
        };
      default:
        return baseStyle;
    }
  }

  return baseStyle;
};

const styles = {
  navbar: {
    backgroundColor: "#001f54",
    color: "#FFFFFF",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    minHeight: "60px",
    display: "flex",
    alignItems: "center",
  },
  navbarScrolled: {
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.15)",
    backgroundColor: "rgba(0, 31, 84, 0.95)",
    backdropFilter: "blur(10px)",
  },
  // Centered container
  container: {
    maxWidth: `${MAX_WIDTH}px`,
    width: "100%",
    margin: "0 auto",
    padding: "0 2rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "60px",
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    zIndex: 1002,
  },
  logoLink: {
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
  },
  logoWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "1px",
  },
  logo: {
    height: "32px",
    width: "auto",
    cursor: "pointer",
    transition: "transform 0.3s ease",
  },
  logoText: {
    color: "#FFD700",
    fontSize: "0.65rem",
    fontWeight: "600",
    letterSpacing: "0.5px",
    marginTop: "1px",
  },
  navLinks: {
    listStyleType: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    gap: "1rem",
    alignItems: "center",
  },
  link: {
    color: "#FFFFFF",
    textDecoration: "none",
    fontWeight: "500",
    transition: "all 0.2s ease",
    position: "relative",
    fontFamily: "inherit",
    background: "none",
    border: "none",
    cursor: "pointer",
  },
  desktopLink: {
    padding: "0.4rem 0.6rem",
    borderRadius: "4px",
    fontSize: "0.85rem",
    letterSpacing: "0.2px",
  },
  mobileLink: {
    display: "block",
    padding: "0.75rem 1rem",
    fontSize: "1rem",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "6px",
    marginBottom: "0.2rem",
    transition: "all 0.2s ease",
    width: "100%",
    textAlign: "left",
  },
  activeLink: {
    color: "#4cc9f0",
    backgroundColor: "rgba(76, 201, 240, 0.1)",
    borderLeft: "3px solid #4cc9f0",
  },
  hamburger: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "36px",
    width: "36px",
    padding: "0.4rem",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "transparent",
    cursor: "pointer",
    zIndex: 1002,
    transition: "background-color 0.2s ease",
  },
  overlay: {
    position: "fixed",
    top: "60px",
    left: 0,
    width: "100%",
    height: "calc(100vh - 60px)",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(4px)",
    zIndex: 999,
  },
};

export default Navbar;